<template>
    <div class="recharge-page">
        <div class="recharge-container glass-effect">
            <div class="page-header">
                <Button icon="ios-arrow-back" @click="$router.back()" type="text">返回</Button>
                <h2>积分充值</h2>
                <div class="current-balance">
                    余额: <span class="val">{{ balance }}</span> 积分
                </div>
            </div>

            <div class="recharge-content">
                <div class="section-title">选择充值金额 <span class="rate-tip">1元 = 100 积分</span></div>

                <Row :gutter="16">
                    <Col v-for="pkg in packages" :key="pkg.amount" :xs="12" :sm="8">
                    <div class="package-card" :class="{ active: selectedPkg?.amount === pkg.amount }"
                        @click="selectedPkg = pkg">
                        <div class="points">{{ pkg.total }} 积分</div>
                        <div class="price">¥{{ (pkg.amount / 100).toFixed(2) }}</div>
                        <div class="bonus" v-if="pkg.bonus > 0">赠 {{ pkg.bonus }} 积分</div>
                    </div>
                    </Col>
                </Row>

                <div class="actions" v-if="selectedPkg">
                    <Divider />
                    <div class="summary">
                        最终金额：<span class="final-price">¥{{ (selectedPkg.amount / 100).toFixed(2) }}</span>
                    </div>
                    <Button type="primary" size="large" :loading="loading" @click="handleRecharge" long class="pay-btn">
                        立即支付
                    </Button>
                </div>
            </div>
        </div>

        <!-- QR Modal -->
        <Modal v-model="qrModal" title="积分充值支付" footer-hide :mask-closable="false" width="400"
            class="recharge-premium-modal">
            <div class="qr-box">
                <div class="pay-method-header">
                    <img src="@/assets/weixin_pay_logo.png" class="wx-logo" alt="WeChat Pay" />
                    <span class="pay-text">微信扫码支付</span>
                </div>

                <div class="qr-wrapper">
                    <div class="qr-border">
                        <img :src="rechargeData.qr_url" alt="QR Code" class="qr-img" />
                    </div>
                    <p class="pay-instruction">请使用微信“扫一扫”完成支付</p>
                </div>

                <div class="order-info-card">
                    <div class="info-row">
                        <span class="label">充值项目</span>
                        <span class="value">{{ rechargeData.total_points }} 积分</span>
                    </div>
                    <div class="info-row">
                        <span class="label">应付金额</span>
                        <span class="value amount">¥{{ (rechargeData.amount / 100).toFixed(2) }}</span>
                    </div>
                </div>

                <div class="pay-status-alert">
                    <Icon type="ios-loading" class="is-loading" />
                    <span>支付成功后系统将自动到账</span>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/utils/api'
import { Message, Notice } from 'view-ui-plus'

const balance = ref(0)
const packages = ref([])
const selectedPkg = ref(null)
const loading = ref(false)
const qrModal = ref(false)
const rechargeData = ref({})

const fetchBalance = async () => {
    try {
        const res = await api.get('/api/points/balance')
        balance.value = res.data.total_points
    } catch (e) { }
}

const fetchPackages = async () => {
    try {
        const res = await api.get('/api/points/recharge/packages')
        packages.value = res.data.packages || []
        if (packages.value.length > 0) selectedPkg.value = packages.value[1] || packages.value[0]
    } catch (e) {
        Message.error('获取充值包失败')
    }
}

const handleRecharge = async () => {
    if (!selectedPkg.value) return
    loading.value = true
    try {
        const res = await api.post('/api/points/recharge', { amount: selectedPkg.value.amount })
        rechargeData.value = res.data
        qrModal.value = true
        startPolling(res.data.out_trade_no)
    } catch (e) {
        Message.error('创建订单失败')
    } finally {
        loading.value = false
    }
}

const startPolling = (no) => {
    const timer = setInterval(async () => {
        if (!qrModal.value) {
            clearInterval(timer)
            return
        }
        try {
            const res = await api.get('/api/points/balance')
            if (res.data.total_points > balance.value) {
                Notice.success({ title: '充值成功', desc: '积分已存入账户' })
                balance.value = res.data.total_points
                qrModal.value = false
                clearInterval(timer)
            }
        } catch (e) { }
    }, 3000)
    setTimeout(() => clearInterval(timer), 300000)
}

onMounted(() => {
    fetchBalance()
    fetchPackages()
})
</script>

<style scoped>
.recharge-page {
    min-height: calc(100vh - 64px);
    background: #f8f9fa;
    padding: 40px 20px;
}

.recharge-container {
    max-width: 800px;
    margin: 0 auto;
    background: #fff;
    border-radius: 16px;
    padding: 30px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.glass-effect {
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(10px);
}

.page-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 30px;
    border-bottom: 1px solid #f0f2f5;
    padding-bottom: 20px;
}

.page-header h2 {
    flex: 1;
    margin: 0;
}

.current-balance {
    font-size: 14px;
    color: #808695;
}

.current-balance .val {
    font-size: 20px;
    font-weight: 700;
    color: #3b82f6;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px;
}

.rate-tip {
    font-size: 12px;
    color: #909399;
    font-weight: normal;
    margin-left: 10px;
}

.package-card {
    border: 2px solid #f0f2f5;
    border-radius: 12px;
    padding: 24px 16px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 16px;
    background: #fff;
    position: relative;
}

.package-card:hover {
    border-color: #3b82f6;
    transform: translateY(-4px);
}

.package-card.active {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
}

.points {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
}

.price {
    font-size: 16px;
    color: #3b82f6;
    font-weight: 600;
    margin-top: 8px;
}

.bonus {
    position: absolute;
    top: -10px;
    right: -5px;
    background: #ff9900;
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
}

.actions {
    margin-top: 20px;
    text-align: right;
}

.summary {
    margin-bottom: 20px;
    font-size: 16px;
}

.final-price {
    font-size: 32px;
    font-weight: 800;
    color: #ed4014;
}

.pay-btn {
    height: 50px;
    font-size: 18px;
    font-weight: 700;
    border-radius: 25px;
}

.qr-box {
    text-align: center;
    padding: 10px 0 0;
}

.pay-method-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
}

.wx-logo {
    height: 22px;
}

.pay-text {
    font-size: 16px;
    font-weight: 700;
    color: #07c160;
}

.qr-wrapper {
    margin-bottom: 20px;
}

.qr-border {
    display: inline-block;
    padding: 10px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 12px;
}

.qr-img {
    width: 180px;
    height: 180px;
    display: block;
}

.pay-instruction {
    margin-top: 10px;
    font-size: 13px;
    color: #808695;
}

.order-info-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.info-row .label {
    color: #808695;
    font-size: 13px;
}

.info-row .value {
    font-weight: 600;
    color: #1c2438;
}

.info-row .amount {
    color: #ed4014;
    font-size: 18px;
}

.pay-status-alert {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #19be6b;
    font-size: 13px;
    font-weight: 500;
    background: #f6ffed;
    padding: 10px;
    border-radius: 8px;
    margin-bottom: 10px;
}

:deep(.recharge-premium-modal .ivu-modal-content) {
    border-radius: 20px;
}

:deep(.recharge-premium-modal .ivu-modal-header-inner) {
    font-weight: 700;
}

.is-loading {
    animation: ani-demo-spin 1s linear infinite;
    font-size: 16px;
}

@keyframes ani-demo-spin {
    from {
        transform: rotate(0deg);
    }

    50% {
        transform: rotate(180deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
